import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import { apiService } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import styled from 'styled-components';
import { default as Button } from '../components/Button';
import { toast } from 'react-hot-toast';
import { getAppVersion } from '../serviceWorkerRegistration';


const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  background-image: url('/images/login-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: auto;
  padding: 1rem;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 0;
  }
`;


const LoginWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (min-width: 640px) {
    max-width: 28rem;
  }
`;



const VersionFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  
  img {
    max-width: 200px;
    width: 100%;
    height: auto;
    margin-bottom: 0.5rem;
    
    @media (max-width: 480px) {
      max-width: 160px;
    }
  }
  
  h1 {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.text};
    margin: 0.5rem 0 0;
    font-weight: 600;
  }
`;

const LoginCard = styled.div`
  background: white;
  padding: 2.5rem 2rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  position: relative;
`;

const PoweredBy = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  
  img {
    height: 20px;
    width: auto;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
  
  @media (max-width: 480px) {
    img {
      height: 16px;
    }
  }
`;


const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? 'white' : theme.colors.textSecondary};
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primaryDark : theme.colors.primaryBackground};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 1px solid ${({ $hasError, theme }) => $hasError ? '#ef4444' : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all 0.2s ease;
  background-color: ${({ $hasError }) => $hasError ? '#fef2f2' : 'white'};

  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) => $hasError ? '#ef4444' : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ $hasError, theme }) => $hasError ? '#fecaca' : theme.colors.primaryBackground};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ErrorMessage = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '⚠️';
    font-size: 1rem;
  }
`;

const FieldError = styled.div`
  color: #dc2626;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::before {
    content: '•';
    font-weight: bold;
  }
`;



const LoginPage: React.FC = () => {
  const [loginType, setLoginType] = useState<'credentials' | 'pin'>('pin');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const errorRef = useRef<string>('');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // Force re-render when error changes to ensure proper display
  const [forceRender, setForceRender] = useState(0);
  useEffect(() => {
    if (error) {
      setForceRender(prev => prev + 1);
    }
  }, [error]);

  const clearErrors = () => {
    setError('');
    setFieldErrors({});
    errorRef.current = '';
  };

  const handleLoginTypeChange = (type: 'credentials' | 'pin') => {
    setLoginType(type);
    clearErrors();
    // Clear sensitive fields when switching
    setPassword('');
    setPin('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't clear errors here - let them persist until user starts typing
    setLoading(true);

    try {
      let response;
      if (loginType === 'credentials') {
        response = await apiService.login(identifier, password);
      } else {
        response = await apiService.loginWithPin(identifier, pin);
      }

      // Convert API response user to auth context user format
      const user = {
        id: response.user.id,
        username: response.user.name, // Map name to username
        email: response.user.email,
        role: response.user.role
      };
      setUser(user);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      
      // Set error state immediately and also in ref
      setError(errorMessage);
      errorRef.current = errorMessage;
      
      // Show toast for immediate feedback
      toast.error(errorMessage);
      
      // Clear form fields on credential mismatch
      if (errorMessage.includes('Invalid') || errorMessage.includes('credentials')) {
        setPassword('');
        setPin('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <LoginWrapper>
        <LoginCard>
          <LogoContainer>
            <img 
              src="/images/logo-ex.png" 
              alt="Logo" 
            />
            <h1>Welcome Back</h1>
          </LogoContainer>
          
          
          <TabContainer>
            <Tab $active={loginType === 'pin'} onClick={() => handleLoginTypeChange('pin')}>
              PIN
            </Tab>
            <Tab $active={loginType === 'credentials'} onClick={() => handleLoginTypeChange('credentials')}>
              Credentials
            </Tab>
          </TabContainer>
          <StyledForm onSubmit={handleSubmit} noValidate>
            {error && (
              <ErrorMessage key={`error-${forceRender}`}>
                {error}
              </ErrorMessage>
            )}
            
            
            <FormGroup>
              <Label htmlFor="identifier">Email or Username</Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => {
                  setIdentifier(e.target.value);
                  // Clear errors when user starts typing
                  if (error) {
                    setError('');
                    errorRef.current = '';
                  }
                  if (fieldErrors.identifier) {
                    setFieldErrors(prev => ({ ...prev, identifier: '' }));
                  }
                }}
                placeholder="Enter your email or username"
                required
                $hasError={!!fieldErrors.identifier}
              />
              {fieldErrors.identifier && <FieldError>{fieldErrors.identifier}</FieldError>}
            </FormGroup>
            
            {loginType === 'credentials' ? (
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    // Clear errors when user starts typing
                    if (error) {
                      setError('');
                      errorRef.current = '';
                    }
                    if (fieldErrors.password) {
                      setFieldErrors(prev => ({ ...prev, password: '' }));
                    }
                  }}
                  placeholder="Enter your password"
                  required
                  $hasError={!!fieldErrors.password}
                />
                {fieldErrors.password && <FieldError>{fieldErrors.password}</FieldError>}
              </FormGroup>
            ) : (
              <FormGroup>
                <Label htmlFor="pin">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value);
                    // Clear errors when user starts typing
                    if (error) {
                      setError('');
                      errorRef.current = '';
                    }
                    if (fieldErrors.pin) {
                      setFieldErrors(prev => ({ ...prev, pin: '' }));
                    }
                  }}
                  placeholder="Enter your PIN"
                  required
                  maxLength={6}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  $hasError={!!fieldErrors.pin}
                />
                {fieldErrors.pin && <FieldError>{fieldErrors.pin}</FieldError>}
              </FormGroup>
            )}
            <Button
              $variant="primary"
              $size="lg"
              $fullWidth
              type="submit"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Login'}
            </Button>
          </StyledForm>
          <PoweredBy>
            <img 
              src="/images/powered-by.png" 
              alt="Powered by" 
            />
          </PoweredBy>
        </LoginCard>
      </LoginWrapper>
      <VersionFooter>
        v{getAppVersion()}
      </VersionFooter>
    </Container>
  );
};

export default LoginPage;